import { useEffect, useState, type FormEvent } from "react";
import { TableSkeleton } from "@/components/Loading";
import { PageHeader } from "@/components/PageHeader";
import { UserAvatar } from "@/components/UserAvatar";
import { Card, CardHeader } from "@/components/ui/card";
import { screens } from "../screens";
import type { AuditEventType, AuditFilter, AuditLogResponse } from "../../shared/audit";
import {
  AUDIT_EVENT_TYPES, auditActorText, auditEventLabel, auditOutcomeLabel, auditTargetText, describeAuditEvent, formatAuditTime,
} from "../../shared/audit";
import { ErrorAlert } from "@/components/ErrorAlert";
import { Button } from "@/components/ui/button";
import { NativeSelect } from "@/components/ui/native-select";
import { Pager } from "@/components/Pager";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { errorText, getAuditLog, getAuditXlsx } from "@/lib/client";

const PAGE_SIZE = 50;

/** Ekranda "kurulum" yazılır (auditActorText); API'ye `setup` gider. */
export const userParam = (u: string | undefined) => (u?.toLowerCase() === "kurulum" ? "setup" : u);

/** Ayrıntı metni (denetim verisi ve Excel tam parmak izini taşır). Ekranda parmak izinin ilk 8 hanesi font-mono, tamamı title'da. */
export function Detail({ type, d }: { type: AuditEventType; d: Record<string, unknown> }) {
  const text = describeAuditEvent(type, d);
  const hash = typeof d.fileHash === "string" && d.fileHash.length > 8 ? d.fileHash : "";
  const i = hash ? text.indexOf(hash) : -1;
  if (i < 0) return <>{text}</>;
  return <>{text.slice(0, i)}<span className="font-mono text-xs" title={hash}>{hash.slice(0, 8)}</span>{text.slice(i + hash.length)}</>;
}

/** E8 (spec §13). Read-only: no edit or delete anywhere on this screen. */
export function AuditLogScreen() {
  const [filter, setFilter] = useState<AuditFilter>({}); // applied filter
  const [page, setPage] = useState(1);
  const [data, setData] = useState<AuditLogResponse | null>(null);
  const [error, setError] = useState("");
  const [exportError, setExportError] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setError(""); setLoading(true);
    getAuditLog(filter, page, PAGE_SIZE).then((d) => live && setData(d), (e) => live && setError(errorText(e))).finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [filter, page]);

  function apply(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const v = (k: string) => String(f.get(k) ?? "").trim() || undefined;
    const user = v("user");
    setFilter({ user: userParam(user), eventType: v("eventType") as AuditEventType | undefined, from: v("from"), to: v("to") });
    setPage(1);
  }

  async function download() {
    setBusy(true);
    setExportError("");
    try {
      const { blob, fileName } = await getAuditXlsx(filter);
      const url = URL.createObjectURL(blob);
      Object.assign(document.createElement("a"), { href: url, download: fileName }).click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setExportError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="flex min-w-0 flex-col gap-4 p-6">
      <PageHeader title="Denetim Logu" description={screens.find((s) => s.id === "E8")!.description}
        actions={<Button type="button" variant="outline" disabled={busy} onClick={() => void download()}>{busy ? "Hazırlanıyor…" : "Excel olarak indir"}</Button>} />
      <Card className="min-w-0 gap-0 overflow-clip p-0">
      <CardHeader className="border-b px-4 py-3 [.border-b]:pb-3">
      <h2 className="sr-only">Denetim kayıtları</h2>
      <form onSubmit={apply} onReset={() => { setFilter({}); setPage(1); }} className="flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-2">
          <Label htmlFor="user">Kullanıcı</Label>
          <Input id="user" name="user" className="w-56" placeholder="kullanıcı adı ya da kurulum" />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="eventType">Olay tipi</Label>
          <NativeSelect id="eventType" name="eventType" defaultValue="">
            <option value="">Tümü</option>
            {AUDIT_EVENT_TYPES.map((t) => <option key={t} value={t}>{auditEventLabel[t]}</option>)}
          </NativeSelect>
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="from">Başlangıç günü</Label>
          <Input id="from" name="from" type="date" />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="to">Bitiş günü</Label>
          <Input id="to" name="to" type="date" />
        </div>
        <Button type="submit">Filtrele</Button>
        <Button type="reset" variant="outline">Temizle</Button>
      </form>
      </CardHeader>
      {exportError && <div className="p-4"><ErrorAlert>{exportError}</ErrorAlert></div>}
      {error && <div className="p-4"><ErrorAlert>{error}</ErrorAlert></div>}
      {!data && !error && <div aria-busy="true" className="p-4"><TableSkeleton rows={8} cols={6} /></div>}
      {data && (
        <div className="flex min-w-0 flex-col transition-opacity aria-busy:opacity-50" aria-busy={loading}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="bg-muted/60 text-xs font-medium text-muted-foreground pl-4">Zaman</TableHead><TableHead className="bg-muted/60 text-xs font-medium text-muted-foreground">Kullanıcı</TableHead><TableHead className="bg-muted/60 text-xs font-medium text-muted-foreground">Olay</TableHead>
                <TableHead className="bg-muted/60 text-xs font-medium text-muted-foreground">Hedef</TableHead><TableHead className="bg-muted/60 text-xs font-medium text-muted-foreground">Sonuç</TableHead><TableHead className="bg-muted/60 text-xs font-medium text-muted-foreground pr-4">Ayrıntı</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-muted-foreground">Bu filtreyle kayıt yok.</TableCell></TableRow>
              )}
              {data.items.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="whitespace-nowrap pl-4 tabular-nums">{formatAuditTime(r.createdAt)}</TableCell>
                  <TableCell><span className="flex items-center gap-2"><UserAvatar name={r.actor === "setup" ? null : r.actor} />{auditActorText(r.actor)}</span></TableCell>
                  <TableCell>{auditEventLabel[r.eventType]}</TableCell>
                  <TableCell>{auditTargetText(r.target)}</TableCell>
                  <TableCell>{auditOutcomeLabel[r.outcome]}</TableCell>
                  <TableCell className="min-w-72 whitespace-normal pr-4"><Detail type={r.eventType} d={r.detail} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="border-t px-4 py-2"><Pager page={page} total={data.total} pageSize={data.pageSize} onPage={setPage} showTotal /></div>
        </div>
      )}
      </Card>
    </main>
  );
}
